// ZustandGenericFactory.ts
import { createStore, StoreApi, StateCreator } from 'zustand';
import React, { createContext, useContext, ReactNode } from 'react';
import { useStore } from 'zustand';
// --- Slice Factory ---
export function createGenericSlice<T extends object>(
  stateKey: string,
  setupFnName: string
): StateCreator<{ [key: string]: any }> {
  return (set, get) => {
    const updateField = <K extends keyof T>(key: K, value: T[K]) => {
      set((state: any) =>
        state[stateKey]
          ? {
              [stateKey]: {
                ...state[stateKey],
                [key]: value,
              },
            }
          : state
      );
    };

    return {
      [stateKey]: null,

      [setupFnName]: (payload: T) => {
        set({ [stateKey]: payload });
      },

      // Auto-generate updateX methods (for primitives only)
      ...Object.fromEntries(
        Object.keys(({} as T)).map((key) => [
          `Update${key.charAt(0).toUpperCase()}${key.slice(1)}`,
          (value: any) => updateField(key as keyof T, value),
        ])
      ),
    };
  };
}

// --- Generic Store Factory ---
export function createScopedStore<T extends object>(
  sliceCreator: StateCreator<any>
): StoreApi<any> {
  return createStore(sliceCreator);
}

export function createZustandContext<TState>() {
  const Context = createContext<StoreApi<TState> | null>(null);

  const Provider: React.FC<{ store: StoreApi<TState>; children: ReactNode }> = ({ store, children }) => {
    return <Context.Provider value={store}>{children}</Context.Provider>;
  };

  // Fix: add comma to disambiguate generic from JSX
  const useGenericStore = <T,>(selector: (state: TState) => T): T => {
    const store = useContext(Context);
    if (!store) throw new Error('Zustand provider is missing');
    return useStore(store, selector);
  };

  const useScopedStore = (): StoreApi<TState> => {
    const store = useContext(Context);
    if (!store) throw new Error('Zustand provider is missing');
    return store;
  };

  return { Provider, useStoreSelector:useGenericStore, useScopedStore };
}

